import { logger } from '@/lib/logger';

/**
 * Environment variable validation utility
 * Ensures all required environment variables are present at startup
 */

interface RequiredEnvVars {
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
  VITE_CLERK_PUBLISHABLE_KEY: string;
}

interface OptionalEnvVars {
  VITE_PUBLIC_APP_URL?: string;
  VITE_ENVIRONMENT?: string;
  VITE_EDGE_FUNCTION_URL?: string;
}

type EnvVars = RequiredEnvVars & OptionalEnvVars;
type ClientEnvKey = keyof RequiredEnvVars | keyof OptionalEnvVars | 'MODE';

class EnvironmentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EnvironmentError';
  }
}

function readClientEnv(key: ClientEnvKey): string | undefined {
  switch (key) {
    case 'VITE_SUPABASE_URL':
      return import.meta.env.VITE_SUPABASE_URL;
    case 'VITE_SUPABASE_ANON_KEY':
      return import.meta.env.VITE_SUPABASE_ANON_KEY;
    case 'VITE_CLERK_PUBLISHABLE_KEY':
      return import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
    case 'VITE_PUBLIC_APP_URL':
      return import.meta.env.VITE_PUBLIC_APP_URL;
    case 'VITE_ENVIRONMENT':
      return import.meta.env.VITE_ENVIRONMENT;
    case 'VITE_EDGE_FUNCTION_URL':
      return import.meta.env.VITE_EDGE_FUNCTION_URL;
    case 'MODE':
      return import.meta.env.MODE;
    default:
      return undefined;
  }
}

/**
 * Gets the current environment
 */
export function getEnvironment(): 'development' | 'staging' | 'production' {
  const env = readClientEnv('VITE_ENVIRONMENT') || readClientEnv('MODE');
  if (env === 'production' || env === 'staging') return env;
  return 'development';
}

/**
 * Validates that all required environment variables are present
 * @throws {EnvironmentError} If any required variables are missing
 */
export function validateRequiredEnvVars(): void {
  const environment = getEnvironment();
  const required: (keyof RequiredEnvVars)[] = [
    'VITE_SUPABASE_URL',
    'VITE_SUPABASE_ANON_KEY',
    'VITE_CLERK_PUBLISHABLE_KEY',
  ];

  const missing: string[] = [];
  const warnings: string[] = [];

  // Check required variables
  for (const key of required) {
    if (!readClientEnv(key)) {
      missing.push(key);
    }
  }

  // Environment-specific validations
  if (environment === 'production') {
    // Production should use production URLs
    if (readClientEnv('VITE_SUPABASE_URL')?.includes('localhost')) {
      warnings.push('Production environment using localhost Supabase URL!');
    }
    if (!readClientEnv('VITE_CLERK_PUBLISHABLE_KEY')?.startsWith('pk_live_')) {
      warnings.push('Production should use pk_live_ Clerk key!');
    }
  } else if (environment === 'development') {
    // Development should use local URLs
    const supabaseUrl = readClientEnv('VITE_SUPABASE_URL');
    if (!supabaseUrl?.includes('localhost') && !supabaseUrl?.includes('127.0.0.1')) {
      logger.warn('⚠️ Development environment using remote Supabase URL');
    }
  }

  // Show warnings
  if (warnings.length > 0) {
    logger.warn('⚠️ Environment configuration warnings:', warnings);
  }

  // Throw error for missing required variables
  if (missing.length > 0) {
    throw new EnvironmentError(
      `Missing required environment variables: ${missing.join(', ')}\n` +
        `Please check your environment configuration.\n` +
        `See .env.example for reference and documentation.\n` +
        `\n` +
        `For local development, copy .env.example to .env.development\n` +
        `For staging/production, set variables in Vercel dashboard.`,
    );
  }
}

/**
 * Gets a required environment variable or throws an error
 * @param key - The environment variable key
 * @returns The environment variable value
 * @throws {EnvironmentError} If the variable is not set
 */
export function getRequiredEnvVar(key: keyof RequiredEnvVars): string {
  const value = readClientEnv(key);
  if (!value) {
    throw new EnvironmentError(
      `Required environment variable ${key} is not set.\n` + `Please add it to your .env file.`,
    );
  }
  return value;
}

/**
 * Gets an optional environment variable with a fallback
 * @param key - The environment variable key
 * @param fallback - The fallback value if not set
 * @returns The environment variable value or fallback
 */
export function getOptionalEnvVar(key: keyof OptionalEnvVars, fallback: string = ''): string {
  return readClientEnv(key) || fallback;
}

/**
 * Gets all environment variables with validation
 * @returns Object containing all environment variables
 */
export function getEnvVars(): EnvVars {
  validateRequiredEnvVars();

  return {
    VITE_SUPABASE_URL: getRequiredEnvVar('VITE_SUPABASE_URL'),
    VITE_SUPABASE_ANON_KEY: getRequiredEnvVar('VITE_SUPABASE_ANON_KEY'),
    VITE_CLERK_PUBLISHABLE_KEY: getRequiredEnvVar('VITE_CLERK_PUBLISHABLE_KEY'),
    VITE_PUBLIC_APP_URL: getOptionalEnvVar('VITE_PUBLIC_APP_URL'),
    VITE_ENVIRONMENT: getOptionalEnvVar('VITE_ENVIRONMENT', getEnvironment()),
    VITE_EDGE_FUNCTION_URL: getOptionalEnvVar('VITE_EDGE_FUNCTION_URL'),
  };
}

/**
 * Checks if the application is running in development mode
 */
export function isDevelopment(): boolean {
  return import.meta.env.DEV;
}

/**
 * Checks if the application is running in production mode
 */
export function isProduction(): boolean {
  return import.meta.env.PROD;
}

/**
 * Logs environment status (only in development)
 */
export function logEnvironmentStatus(): void {
  const environment = getEnvironment();

  if (isDevelopment()) {
    const supabaseUrl = readClientEnv('VITE_SUPABASE_URL');
    const clerkKey = readClientEnv('VITE_CLERK_PUBLISHABLE_KEY');
    logger.debug('🔐 Environment Configuration:');
    logger.debug('📍 Environment:', environment);
    logger.debug('🌐 Supabase URL:', supabaseUrl?.substring(0, 30) + '...');
    logger.debug('🔑 Clerk Key Type:', clerkKey?.startsWith('pk_test_') ? 'Test' : 'Production');
    logger.debug('✅ Required Variables:', {
      SUPABASE_URL: !!supabaseUrl,
      SUPABASE_ANON_KEY: !!readClientEnv('VITE_SUPABASE_ANON_KEY'),
      CLERK_KEY: !!clerkKey,
    });

    // Environment sanity check
    const isLocalSupabase = supabaseUrl?.includes('localhost');
    if (environment === 'development' && !isLocalSupabase) {
      logger.warn('⚠️ Development environment but using remote Supabase');
    }
    if (environment === 'production' && isLocalSupabase) {
      logger.error('🚨 Production environment using localhost Supabase!');
    }
  }
}
